import { PushNotifications, type PushNotificationSchema, type ActionPerformed, type Token } from '@capacitor/push-notifications';
import { axiosAppServices } from 'utils/axios';
import { isNative, nativePlatform } from './native';

/** אותו מזהה ערוץ שהשרת שולח ב-AndroidConfig.Notification.ChannelId */
const ANDROID_CHANNEL_ID = 'high_importance_channel';
const STORAGE_KEY_TOKEN = 'funner:pushToken';
const STORAGE_KEY_DEVICE = 'funner:deviceId';

export type PushTapHandler = (data: Record<string, string>) => void;
export type PushForegroundHandler = (notification: PushNotificationSchema) => void;

let initialized = false;
let listenersAttached = false;
let onTap: PushTapHandler | null = null;
let onForeground: PushForegroundHandler | null = null;

const getDeviceId = (): string => {
  try {
    let id = localStorage.getItem(STORAGE_KEY_DEVICE);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 128);
      localStorage.setItem(STORAGE_KEY_DEVICE, id);
    }
    return id;
  } catch {
    return 'unknown-device';
  }
};

const readStoredToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY_TOKEN);
  } catch {
    return null;
  }
};

const storeToken = (token: string | null) => {
  try {
    if (token) localStorage.setItem(STORAGE_KEY_TOKEN, token);
    else localStorage.removeItem(STORAGE_KEY_TOKEN);
  } catch {
    /* ignore */
  }
};

const registerTokenOnServer = async (token: string) => {
  await axiosAppServices.post('/api/push/tokens', {
    token,
    platform: nativePlatform,
    deviceId: getDeviceId(),
    appVersion: import.meta.env.VITE_APP_VERSION ?? null,
  });
};

/**
 * מבקש הרשאה, נרשם ל-FCM ושולח את הטוקן לשרת. לקרוא רק אחרי שהמשתמש מחובר
 * (הרישום בשרת דורש JWT). בטוח לקרוא יותר מפעם אחת.
 */
export const initNativePush = async (handlers: { onTap: PushTapHandler; onForeground: PushForegroundHandler }) => {
  if (!isNative) return;
  onTap = handlers.onTap;
  onForeground = handlers.onForeground;

  if (!listenersAttached) {
    listenersAttached = true;

    await PushNotifications.addListener('registration', (token: Token) => {
      storeToken(token.value);
      registerTokenOnServer(token.value).catch((err) => console.error('[push] token register failed', err));
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('[push] registration error', err);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      onForeground?.(notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      onTap?.((action.notification.data ?? {}) as Record<string, string>);
    });
  }

  if (initialized) {
    // טוקן קיים — רק לרענן את ה-last_seen בשרת (למשל אחרי התחברות מחדש)
    const existing = readStoredToken();
    if (existing) registerTokenOnServer(existing).catch(() => undefined);
    return;
  }

  try {
    if (nativePlatform === 'android') {
      await PushNotifications.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: 'הודעות נכנסות',
        description: 'הודעות WhatsApp חדשות מלקוחות',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
    }

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      console.warn('[push] permission not granted:', perm.receive);
      return;
    }

    await PushNotifications.register();
    initialized = true;
  } catch (err) {
    console.error('[push] init failed', err);
  }
};

/** בהתנתקות: מסיר את הטוקן מהשרת כדי שהמכשיר לא ימשיך לקבל התראות של המשתמש הקודם */
export const unregisterNativePush = async () => {
  if (!isNative) return;
  const token = readStoredToken();
  if (!token) return;
  try {
    await axiosAppServices.delete('/api/push/tokens', { data: { token } });
  } catch (err) {
    console.error('[push] token unregister failed', err);
  }
};

/** מנקה את ההתראות מהמגש (למשל כשנכנסים לשיחה) */
export const clearDeliveredNotifications = async () => {
  if (!isNative) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch {
    /* ignore */
  }
};
